'use client';

import React, { useRef, useEffect, useState, useCallback } from 'react';
import Image from 'next/image';
import { cn } from '@/utils/cn';

export interface ScrollScrubVideoProps {
  src: string;
  posterSrc: string;
  firefoxSrc?: string;
  mobileSrc?: string;
  minWidth?: number;
  scrubPages?: number;
  scrubPagesMobile?: number;
  zoomDuration?: number;
  loadFadeDuration?: number;
  unzoomedMarginLeft?: string;
  unzoomedMarginRight?: string;
  unzoomedMarginTop?: string;
  unzoomedMarginBottom?: string;
  className?: string;
}

interface ComponentData {
  lower: number;
  upper: number;
  video: HTMLVideoElement | null;
}

// Global state for managing multiple instances
const componentDataMap = new WeakMap<HTMLDivElement, ComponentData>();
const allScrollScrubComponents = new Set<HTMLDivElement>();
const observedElements = new Set<HTMLDivElement>();
let activeScrollScrubComponent: HTMLDivElement | null = null;
let observer: IntersectionObserver | null = null;
let scrollHandler: (() => void) | null = null;
let resizeHandler: (() => void) | null = null;
let lastMobileSeekTime = 0;

const OVERSCRUB_AVOIDANCE_FACTOR = 0.99;

// Mobile detection utility
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;

  // Check for touch capability and screen size
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;

  // Check user agent for mobile indicators
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = [
    'mobile',
    'android',
    'iphone',
    'ipad',
    'ipod',
    'blackberry',
    'windows phone',
  ];
  const hasMobileUserAgent = mobileKeywords.some((keyword) => userAgent.includes(keyword));

  return hasTouchScreen && (isSmallScreen || hasMobileUserAgent);
};

// Debounce utility
const debounce = (callback: (...args: any[]) => void, frequency = 250) => {
  let timer: NodeJS.Timeout | null = null;
  return (...args: any[]) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => callback(...args), frequency);
  };
};

export const ScrollScrubVideo: React.FC<ScrollScrubVideoProps> = ({
  src,
  posterSrc,
  firefoxSrc,
  mobileSrc,
  minWidth = 0,
  scrubPages = 6,
  scrubPagesMobile = 3,
  zoomDuration = 0.2,
  loadFadeDuration = 0.5,
  className,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null); // Main wrapper element
  const containerRef = useRef<HTMLDivElement>(null); // Sticky video container
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isInView, setIsInView] = useState(false);

  // Get the appropriate video source based on device and browser
  const getVideoSrc = useCallback(() => {
    // Prioritize mobile source on mobile devices
    if (mobileSrc && isMobileDevice()) {
      return mobileSrc;
    }

    // Use Firefox-specific source for Firefox browsers
    if (firefoxSrc && navigator.userAgent.toLowerCase().includes('firefox')) {
      return firefoxSrc;
    }

    // Default to main source
    return src;
  }, [src, firefoxSrc, mobileSrc]);

  // Load video (blob method for desktop, normal loading for mobile)
  const loadVideo = useCallback(async (): Promise<(() => void) | undefined> => {
    const videoSrc = getVideoSrc();
    if (!videoSrc || !videoRef.current) return;

    try {
      if (isMobileDevice()) {
        // Use normal loading for mobile devices to avoid memory issues
        videoRef.current.src = videoSrc;
        videoRef.current.preload = 'auto'; // Load the whole video initially
        const handleCanPlayThrough = () => {
          setIsVideoLoaded(true);
          // Recalculate bounds after video loads and height changes
          setTimeout(() => {
            updateAllScrollScrubComponents();
          }, 100);
        };

        videoRef.current.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
        videoRef.current.load();

        // Store cleanup function for this video element
        const currentVideo = videoRef.current;
        return () => {
          currentVideo.removeEventListener('canplaythrough', handleCanPlayThrough);
        };
      } else {
        // Use blob preloading for desktop for better performance
        const response = await fetch(videoSrc);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        const blobURL = URL.createObjectURL(blob);

        if (videoRef.current) {
          videoRef.current.src = blobURL;
          setIsVideoLoaded(true);
          // Recalculate bounds after video loads and height changes
          setTimeout(() => {
            updateAllScrollScrubComponents();
          }, 100);
        }
      }
    } catch (error) {
      console.error('Error loading video:', error);
      // Fallback to normal loading if blob method fails
      if (videoRef.current) {
        videoRef.current.src = videoSrc;
        videoRef.current.preload = 'auto';
        const handleCanPlayThrough = () => {
          setIsVideoLoaded(true);
          // Recalculate bounds after video loads and height changes
          setTimeout(() => {
            updateAllScrollScrubComponents();
          }, 100);
        };
        videoRef.current.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
        videoRef.current.load();
        return () => {
          videoRef.current?.removeEventListener('canplaythrough', handleCanPlayThrough);
        };
      }
    }

    return undefined; // No cleanup needed for desktop
  }, [getVideoSrc]);

  // Global scroll handler
  const globalHandleScrollEvent = useCallback(() => {
    if (!activeScrollScrubComponent) {
      return;
    }

    const activeWrapperPosition = componentDataMap.get(activeScrollScrubComponent);
    if (!activeWrapperPosition) {
      return;
    }

    const { lower, upper, video } = activeWrapperPosition;
    if (!video) {
      return;
    }

    // On mobile, ensure video metadata is loaded before scrubbing
    if (
      isMobileDevice() &&
      (video.readyState < 1 || isNaN(video.duration) || video.duration === 0)
    ) {
      // Video metadata not ready yet, try to load it
      if (video.readyState === 0) {
        video.load();
      }
      return;
    }

    // Calculate the scroll progress within the active video wrapper
    const progress = Math.max(
      Math.min((window.scrollY - lower) / (upper - lower), OVERSCRUB_AVOIDANCE_FACTOR),
      0
    );
    const seekTime = progress * video.duration;

    if (isFinite(seekTime) && !isNaN(video.duration) && video.duration > 0) {
      // On mobile, add additional checks before seeking
      if (isMobileDevice()) {
        // Throttle mobile seeking to prevent too many operations
        const now = Date.now();
        if (now - lastMobileSeekTime < 16) {
          // ~60fps throttle
          return;
        }
        lastMobileSeekTime = now;

        // Ensure video is ready for seeking
        if (video.readyState >= 2) {
          // HAVE_CURRENT_DATA or higher
          try {
            requestAnimationFrame(() => {
              video.currentTime = seekTime;
            });
          } catch (error) {
            console.error('Mobile seeking error:', error);
          }
        } else {
          console.log('Mobile video not ready for seeking, readyState:', video.readyState);
        }
      } else {
        // Desktop - use requestAnimationFrame as before
        requestAnimationFrame(() => {
          video.currentTime = seekTime;
        });
      }
    }
  }, []);

  // Update all component positions
  const updateAllScrollScrubComponents = useCallback(() => {
    allScrollScrubComponents.forEach((wrapperElement) => {
      const clientRect = wrapperElement.getBoundingClientRect();
      const { y, bottom } = clientRect;
      const componentTopPosition = y + window.scrollY;
      const componentBottomPosition = bottom - window.innerHeight + window.scrollY;

      const video = wrapperElement.querySelector('video') as HTMLVideoElement;

      componentDataMap.set(wrapperElement, {
        lower: componentTopPosition,
        upper: componentBottomPosition,
        video,
      });
    });
  }, []);

  // Component-specific intersection callback
  const handleIntersection = useCallback(
    (isWithinViewport: boolean) => {
      setIsAnimating(true);
      setIsInView(isWithinViewport);

      // Remove animation class after zoom duration
      const delay = zoomDuration * 1000;
      setTimeout(() => {
        setIsAnimating(false);
      }, delay);

      if (isWithinViewport && wrapperRef.current) {
        activeScrollScrubComponent = wrapperRef.current;
        globalHandleScrollEvent();
      }
    },
    [zoomDuration, globalHandleScrollEvent]
  );

  // Global intersection observer callback
  const intersectionObserverCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const isWithinViewport = entry.intersectionRatio === 1;
        const videoContainer = entry.target as HTMLDivElement;

        // Find the wrapper element (parent of the video container)
        const wrapperElement = videoContainer.parentElement;

        // Trigger a custom event that the component can listen to
        const event = new CustomEvent('scrollScrubIntersection', {
          detail: { isWithinViewport },
        });
        videoContainer.dispatchEvent(event);

        if (isWithinViewport && wrapperElement) {
          activeScrollScrubComponent = wrapperElement as HTMLDivElement;
          globalHandleScrollEvent();
        }
      });
    },
    [globalHandleScrollEvent]
  );

  // Initialize global handlers
  useEffect(() => {
    if (!observer) {
      observer = new IntersectionObserver(intersectionObserverCallback, { threshold: 1 });
    }

    if (!scrollHandler) {
      scrollHandler = globalHandleScrollEvent;
      // Use passive listeners on mobile for better performance
      const scrollOptions = isMobileDevice() ? { passive: true } : false;
      document.addEventListener('scroll', scrollHandler, scrollOptions);
    }

    if (!resizeHandler) {
      resizeHandler = debounce(updateAllScrollScrubComponents, 50);
      window.addEventListener('resize', resizeHandler);
      // call updateAllScrollScrubComponents immediately
      updateAllScrollScrubComponents();
    }

    return () => {
      // Cleanup only when no components are left
      if (allScrollScrubComponents.size === 0) {
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        if (scrollHandler) {
          document.removeEventListener('scroll', scrollHandler);
          scrollHandler = null;
        }
        if (resizeHandler) {
          window.removeEventListener('resize', resizeHandler);
          resizeHandler = null;
        }
      }
    };
  }, [intersectionObserverCallback, globalHandleScrollEvent, updateAllScrollScrubComponents]);

  // Handle responsive behavior
  useEffect(() => {
    const checkWidth = () => {
      if (minWidth && window.innerWidth < minWidth) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, [minWidth]);

  // Setup component on mount
  useEffect(() => {
    if (!containerRef.current || !wrapperRef.current) return;

    const container = containerRef.current;
    const wrapper = wrapperRef.current;

    // Track the wrapper element for position calculations
    allScrollScrubComponents.add(wrapper);

    // Listen for intersection events on the container
    const handleIntersectionEvent = (event: CustomEvent) => {
      const { isWithinViewport } = event.detail;
      handleIntersection(isWithinViewport);
    };

    container.addEventListener('scrollScrubIntersection', handleIntersectionEvent as EventListener);

    // Load video and setup observer
    let videoCleanup: (() => void) | undefined;
    if (!isVideoLoaded) {
      loadVideo().then((cleanup) => {
        videoCleanup = cleanup;
        if (observer && container && !isHidden) {
          // Observe the sticky container, not the wrapper
          observer.observe(container);
          observedElements.add(container);
          updateAllScrollScrubComponents();
        }
      });
    }

    return () => {
      container.removeEventListener(
        'scrollScrubIntersection',
        handleIntersectionEvent as EventListener
      );
      if (observer && container) {
        observer.unobserve(container);
        observedElements.delete(container);
      }
      allScrollScrubComponents.delete(wrapper);
      updateAllScrollScrubComponents();

      // Clean up video event listener
      if (videoCleanup) {
        videoCleanup();
      }
    };
  }, [loadVideo, updateAllScrollScrubComponents, isHidden, handleIntersection]);

  if (isHidden) {
    return null;
  }

  const containerStyle = {
    '--scrub-pages': isMobileDevice() ? scrubPagesMobile : scrubPages,
    '--zoom-duration': `${zoomDuration}s`,
    '--load-fade-duration': `${loadFadeDuration}s`,
  } as React.CSSProperties;

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'block min-h-screen scroll-scrub-video',
        isVideoLoaded && 'h-[calc(100vh*var(--scrub-pages))]',
        className
      )}
      style={containerStyle}
    >
      <div
        ref={containerRef}
        className={cn(
          'sticky top-0 h-screen',
          'ml-[var(--unzoomed-margin-left)] mr-[var(--unzoomed-margin-right)]',
          isAnimating && 'transition-all duration-[var(--zoom-duration)]',
          isInView && 'm-0'
        )}
      >
        {/* Poster image wrapper with positioning */}
        <div
          className={cn(
            'absolute left-0 w-full z-0',
            'top-[var(--unzoomed-margin-top)]',
            'h-[calc(100%-var(--unzoomed-margin-top)-var(--unzoomed-margin-bottom))]',
            isAnimating && 'transition-all duration-[var(--zoom-duration)]',
            isInView && 'top-0 h-full'
          )}
        >
          <Image
            src={posterSrc}
            alt="Video poster"
            fill
            className="object-cover"
            sizes="100vw"
            priority
          />
        </div>

        <video
          ref={videoRef}
          className={cn(
            'absolute left-0 w-full object-cover z-10',
            'top-[var(--unzoomed-margin-top)]',
            'h-[calc(100%-var(--unzoomed-margin-top)-var(--unzoomed-margin-bottom))]',
            'opacity-0 transition-opacity duration-[var(--load-fade-duration)]',
            isVideoLoaded && 'opacity-100',
            isAnimating && 'transition-all duration-[var(--zoom-duration)]',
            isInView && 'top-0 h-full'
          )}
          muted
          playsInline
          webkit-playsinline="true"
          controls={false}
          disablePictureInPicture
          preload="auto"
        />
      </div>
    </div>
  );
};

export default ScrollScrubVideo;
